import Send from '@/assets/icons/Send.svg';
import {Button} from '@/components/ui/button';
import {Checkbox} from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {useToast} from '@/hooks/use-toast';
import {QuotationRequest, QuotationRequestSchema} from '@/types/form';
import {zodResolver} from '@hookform/resolvers/zod';
import {LoaderCircle} from 'lucide-react';
import Link from 'next/link';
import {useState} from 'react';
import {useForm} from 'react-hook-form';

export default function QuotationFormSheet({
  onSubmitSucceeded,
}: {
  onSubmitSucceeded?: () => void;
}) {
  const {toast} = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const form = useForm<QuotationRequest>({
    resolver: zodResolver(QuotationRequestSchema),
  });

  const encode = (data: any) => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]),
      )
      .join('&');
  };

  async function onSubmit(data: QuotationRequest) {
    try {
      const payload = {
        'entry.1281100226': data.name,
        'entry.1956414444': data.surname,
        'entry.2074188680': data.company,
        'entry.1892945232': data.role,
        'entry.1225562881': data.email,
        'entry.854530408': data.phone,
        'entry.1225109730': data.users,
      };

      await fetch(
        'https://docs.google.com/forms/u/2/d/e/1FAIpQLSfp5fkCmZiv1ECkdmlnZcT0xI-T8gx6pKUX-X8lAVhnwaQ7dQ/formResponse',
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          method: 'POST',
          mode: 'no-cors',
          body: encode(payload),
        },
      );

      setIsSubmitted(true);
      if (onSubmitSucceeded) {
        onSubmitSucceeded();
      }
    } catch (err) {
      console.error(err);
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore',
        variant: 'destructive',
      });
    }
  }

  return (
    <Sheet>
      <SheetTrigger className="inline-flex items-center justify-center whitespace-nowrap rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 gap-2 bg-primary text-font-color-1 hover:bg-secondary hover:text-primary text-[0.875rem] leading-[1.5rem] font-bold px-8 py-4 min-w-48">
        Richiedi Quotazione
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="max-h-screen overflow-y-auto bg-background-4"
      >
        <SheetHeader className="flex gap-4 max-w-[912px] mx-auto ">
          <SheetTitle>
            Richiesta quotazione Abbonamento “Plus” Multiutente
          </SheetTitle>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 transition-opacity duration-1000 ease-in-out`}
            >
              <FormField
                control={form.control}
                name="name"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isSubmitted}
                        placeholder="Mario"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="surname"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Cognome</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isSubmitted}
                        placeholder="Rossi"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Azienda</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isSubmitted}
                        placeholder="Studio Rossi & Associati"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Azienda presso cui lavori</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Ruolo</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isSubmitted}
                        placeholder="Titolare"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Ruolo che ricopri presso l'azienda indicata
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({field}) => (
                  <FormItem className="flex-1">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        disabled={isSubmitted}
                        placeholder="mario.rossi@normo.ai"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Indirizzo email dove vorresti essere contattato
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({field}) => (
                  <FormItem className="flex-1">
                    <FormLabel>Telefono</FormLabel>
                    <FormControl>
                      <Input
                        type="phone"
                        disabled={isSubmitted}
                        placeholder="+393331234567"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Numero di telefono di riferimento
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="users"
                render={({field}) => (
                  <FormItem className="lg:col-span-2 flex-1">
                    <FormLabel>Numero Utenze</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={isSubmitted}
                    >
                      <FormControl>
                        <SelectTrigger className="h-16 text-lg bg-white border-0">
                          <SelectValue placeholder="Seleziona il numero di utenze" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem className="h-12 text-lg" value="2">
                          2 Utenze
                        </SelectItem>
                        <SelectItem className="h-12 text-lg" value="3">
                          3 Utenze
                        </SelectItem>
                        <SelectItem className="h-12 text-lg" value="4">
                          4 Utenze
                        </SelectItem>
                        <SelectItem className="h-12 text-lg" value="5">
                          5 Utenze
                        </SelectItem>
                        <SelectItem className="h-12 text-lg" value="5+">
                          Più di 5 utenze
                        </SelectItem>
                        <SelectItem
                          className="h-12 text-lg"
                          value="devo_valutare"
                        >
                          Devi ancora valutare
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Numero di utenze desiderate per la quotazione
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="termsAndConditions"
                render={({field}) => (
                  <FormItem className="md:col-span-2 flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        disabled={isSubmitted}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Accetto termini e condizioni</FormLabel>
                      <FormDescription>
                        Dichiaro di aver letto e compreso l'
                        <Link href="/privacy" className="underline">
                          informativa sulla privacy
                        </Link>{' '}
                        e acconsento al trattamento dei miei dati personali
                        secondo i&nbsp;
                        <Link href="/terms" className="underline">
                          termini
                        </Link>{' '}
                        stabiliti.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              {!isSubmitted ? (
                <div className="md:col-span-2 flex justify-center">
                  <Button type="submit" className="flex-1">
                    <span className="pl-2">Invia richiesta</span>
                    {form.formState.isSubmitting ? (
                      <LoaderCircle
                        height={24}
                        width={24}
                        className="animate-spin"
                      />
                    ) : (
                      <Send height={24} width={24} />
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex justify-center items-center p-4 bg-green-200 border border-green-500 text-green-700 rounded text-lg md:col-span-2">
                  Dati inviati con successo! Un nostro esperto ti contatterà
                  entro 48h.
                </div>
              )}
            </form>
          </Form>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}
